import type { TeleopCommands } from "../modules/teleop-commands";
import type { CameraCatalogEntry } from "../modules/teleop-state";
import type { VideoApi } from "../modules/teleop-session";
import { useCameraBootstrap } from "./useCameraBootstrap";
import type { UseCameraBootstrapResult } from "./useCameraBootstrap";
import { useVideoSlots } from "./useVideoSlots";
import type { UseVideoSlotsResult } from "./useVideoSlots";

export interface UseCameraSlotsSession {
  commands: TeleopCommands;
  videoApi: VideoApi | null;
  sessionReady: boolean;
  setStatus: (text: string) => void;
}

export interface UseCameraSlotsRobot {
  cameras: CameraCatalogEntry[];
  ready: boolean;
}

export type UseCameraSlotsResult = UseVideoSlotsResult & UseCameraBootstrapResult;

/**
 * Camera-slot ownership for a page that renders video: streams/metrics (`useVideoSlots`)
 * plus the enable/toggle policy (`useCameraBootstrap`), composed behind one call that
 * takes the whole session/robot results instead of threading 7 individual fields.
 *
 * Purely a composition of the two existing hooks — no behavior change, just fewer
 * things to wire at the call site. A page that doesn't render video (no camera slots
 * at all) simply doesn't call this hook.
 */
export function useCameraSlots({
  session,
  robot,
}: {
  session: UseCameraSlotsSession;
  robot: UseCameraSlotsRobot;
}): UseCameraSlotsResult {
  const video = useVideoSlots({ videoApi: session.videoApi });
  const camera = useCameraBootstrap({
    cameras: robot.cameras,
    commands: session.commands,
    sessionReady: session.sessionReady,
    configReady: robot.ready,
    videoApi: session.videoApi,
    setStatus: session.setStatus,
    setSlotEnabled: video.setSlotEnabled,
  });
  return { ...video, ...camera };
}
